import { useState, useEffect } from 'react';
import { api } from '@/lib/api';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Building2, Bell, Printer, Shield, Save, CheckCircle2, XCircle, Loader2, Globe, Mail, Phone, Receipt, Image, Package, FileText } from 'lucide-react';

interface SystemSetting {
  id: number;
  key: string;
  value: string;
  description?: string;
  type: string;
  category?: string;
  isEditable: boolean;
  createdAt: string;
  updatedAt?: string;
}

const T = {
  title: "الإعدادات",
  subtitle: "إدارة إعدادات النظام والشركة",
  companyInfo: "معلومات الشركة",
  generalSettings: "إعدادات عامة",
  notificationSettings: "إعدادات الإشعارات",
  printSettings: "إعدادات الطباعة",
  securitySettings: "إعدادات الأمان",
  loading: "جاري التحميل...",
  saving: "جاري الحفظ...",
  saved: "تم الحفظ بنجاح",
  error: "حدث خطأ",
  saveChanges: "حفظ التغييرات",
  companyName: "اسم الشركة",
  companyEmail: "البريد الإلكتروني",
  companyPhone: "رقم الهاتف",
  companyAddress: "العنوان",
  companyWebsite: "الموقع الإلكتروني",
  taxNumber: "الرقم الضريبي",
  companyLogo: "رابط الشعار",
  currency: "العملة الافتراضية",
  language: "اللغة الافتراضية",
  timezone: "المنطقة الزمنية",
  dateFormat: "تنسيق التاريخ",
  emailNotifications: "إشعارات البريد",
  smsNotifications: "إشعارات SMS",
  shipmentUpdates: "تحديثات الشحنات",
  paymentAlerts: "تنبيهات المدفوعات",
  dailyReports: "التقارير اليومية",
  paperSize: "حجم الورق",
  includeLogo: "تضمين الشعار",
  includeFooter: "تضمين التذييل",
  showBarcode: "إظهار الباركود",
  showQRCode: "إظهار QR Code",
  passwordPolicy: "سياسة كلمة المرور",
  twoFactor: "المصادقة الثنائية",
  sessionTimeout: "مهلة الجلسة (دقائق)",
  enable2FA: "تفعيل 2FA"
};

export default function SettingsPage() {
  const [activeTab, setActiveTab] = useState('company');
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [saveStatus, setSaveStatus] = useState<'idle' | 'success' | 'error'>('idle');

  const [companyInfo, setCompanyInfo] = useState({
    companyName: '', companyEmail: '', companyPhone: '',
    companyAddress: '', companyWebsite: '', taxNumber: '', companyLogo: ''
  });

  const [generalSettings, setGeneralSettings] = useState({
    currency: 'EGP', language: 'ar', timezone: 'Africa/Cairo', dateFormat: 'DD/MM/YYYY'
  });

  const [notificationSettings, setNotificationSettings] = useState({
    emailNotifications: true, smsNotifications: true,
    shipmentUpdates: true, paymentAlerts: true, dailyReports: true
  });

  const [printSettings, setPrintSettings] = useState({
    paperSize: 'A4', includeLogo: true, includeFooter: true,
    showBarcode: true, showQRCode: false
  });

  const [securitySettings, setSecuritySettings] = useState({
    passwordPolicy: 'strong', sessionTimeout: '30', enable2FA: false
  });

  useEffect(() => { loadSettings(); }, []);

  const loadSettings = async () => {
    setLoading(true);
    try {
      const [companyRes, settingsRes] = await Promise.all([
        api.get('/api/settings/company-info'),
        api.get('/api/settings')
      ]);

      if (companyRes.data?.success) {
        const data = companyRes.data.data;
        setCompanyInfo({
          companyName: data.companyName || '',
          companyEmail: data.email || '',
          companyPhone: data.phone || '',
          companyAddress: data.address || '',
          companyWebsite: data.website || '',
          taxNumber: data.taxNumber || '',
          companyLogo: data.logoUrl || ''
        });
      }

      if (settingsRes.data?.success) {
        const settings = settingsRes.data.data;
        const getVal = (key: string, def: string) => {
          const s = settings.find((x: SystemSetting) => x.key === key);
          return s?.value || def;
        };
        const getBool = (key: string, def: boolean) => {
          const s = settings.find((x: SystemSetting) => x.key === key);
          return s ? s.value.toLowerCase() === 'true' : def;
        };

        setGeneralSettings({
          currency: getVal('Currency', 'EGP'),
          language: getVal('Language', 'ar'),
          timezone: getVal('Timezone', 'Africa/Cairo'),
          dateFormat: getVal('DateFormat', 'DD/MM/YYYY')
        });

        setNotificationSettings({
          emailNotifications: getBool('EmailNotifications', true),
          smsNotifications: getBool('SMSNotifications', true),
          shipmentUpdates: getBool('ShipmentUpdates', true),
          paymentAlerts: getBool('PaymentAlerts', true),
          dailyReports: getBool('DailyReports', true)
        });

        setPrintSettings({
          paperSize: getVal('PaperSize', 'A4'),
          includeLogo: getBool('IncludeLogo', true),
          includeFooter: getBool('IncludeFooter', true),
          showBarcode: getBool('ShowBarcode', true),
          showQRCode: getBool('ShowQRCode', false)
        });

        setSecuritySettings({
          passwordPolicy: getVal('PasswordPolicy', 'strong'),
          sessionTimeout: getVal('SessionTimeout', '30'),
          enable2FA: getBool('Enable2FA', false)
        });
      }
    } catch (e) { console.error(e); }
    finally { setLoading(false); }
  };

  const showSaveStatus = (success: boolean) => {
    setSaveStatus(success ? 'success' : 'error');
    setTimeout(() => setSaveStatus('idle'), 3000);
  };

  const saveCompanyInfo = async () => {
    setSaving(true);
    try {
      await api.put('/api/settings/company-info', {
        companyName: companyInfo.companyName,
        address: companyInfo.companyAddress,
        phone: companyInfo.companyPhone,
        email: companyInfo.companyEmail,
        taxNumber: companyInfo.taxNumber,
        logoUrl: companyInfo.companyLogo
      });
      showSaveStatus(true);
    } catch (e) { showSaveStatus(false); }
    finally { setSaving(false); }
  };

  const saveGeneralSettings = async () => {
    setSaving(true);
    try {
      await api.put('/api/settings/batch', generalSettings);
      showSaveStatus(true);
    } catch (e) { showSaveStatus(false); }
    finally { setSaving(false); }
  };

  const saveNotificationSettings = async () => {
    setSaving(true);
    try {
      const batch: Record<string, string> = {};
      Object.entries(notificationSettings).forEach(([k, v]) => { batch[k] = String(v); });
      await api.put('/api/settings/batch', batch);
      showSaveStatus(true);
    } catch (e) { showSaveStatus(false); }
    finally { setSaving(false); }
  };

  const savePrintSettings = async () => {
    setSaving(true);
    try {
      const batch: Record<string, string> = {};
      Object.entries(printSettings).forEach(([k, v]) => { batch[k] = String(v); });
      await api.put('/api/settings/batch', batch);
      showSaveStatus(true);
    } catch (e) { showSaveStatus(false); }
    finally { setSaving(false); }
  };

  const saveSecuritySettings = async () => {
    setSaving(true);
    try {
      await api.put('/api/settings/batch', securitySettings);
      showSaveStatus(true);
    } catch (e) { showSaveStatus(false); }
    finally { setSaving(false); }
  };

  const inputClass = 'w-full px-4 py-2.5 bg-gray-50 border border-gray-200 rounded-xl focus:outline-none focus:border-slate-500 transition';

  const tabs = [
    { id: 'company', label: T.companyInfo, icon: Building2 },
    { id: 'general', label: T.generalSettings, icon: Globe },
    { id: 'notifications', label: T.notificationSettings, icon: Bell },
    { id: 'printing', label: T.printSettings, icon: Printer },
    { id: 'security', label: T.securitySettings, icon: Shield }
  ];

  const SaveButton = ({ onClick }: { onClick: () => void }) => (
    <div className="flex items-center gap-3 pt-4">
      <Button onClick={onClick} disabled={saving} className="bg-slate-700 hover:bg-slate-800">
        {saving ? <Loader2 className="w-4 h-4 ml-2 animate-spin" /> : <Save className="w-4 h-4 ml-2" />}
        {saving ? T.saving : T.saveChanges}
      </Button>
      {saveStatus === 'success' && <span className="text-green-600 text-sm flex items-center gap-1"><CheckCircle2 className="w-4 h-4" />{T.saved}</span>}
      {saveStatus === 'error' && <span className="text-red-600 text-sm flex items-center gap-1"><XCircle className="w-4 h-4" />{T.error}</span>}
    </div>
  );

  const Toggle = ({ checked, onChange }: { checked: boolean; onChange: (v: boolean) => void }) => (
    <label className="relative inline-flex items-center cursor-pointer">
      <input type="checkbox" checked={checked} onChange={(e) => onChange(e.target.checked)} className="sr-only peer" />
      <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none rounded-full peer peer-checked:bg-slate-600 after:content-[''] after:absolute after:top-[2px] after:right-[2px] after:bg-white after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:after:translate-x-[-20px]"></div>
    </label>
  );

  if (loading) {
    return (
      <div className="p-12 text-center">
        <Loader2 className="w-12 h-12 animate-spin text-slate-600 mx-auto" />
        <p className="text-gray-500 mt-4">{T.loading}</p>
      </div>
    );
  }

  return (
    <div className="space-y-6" dir="rtl">
      <div className="flex items-center justify-between">
        <h1 className="text-3xl font-bold text-gray-900 flex items-center gap-3">
          <div className="w-12 h-12 bg-gradient-to-br from-slate-700 to-slate-900 rounded-xl flex items-center justify-center">
            <Shield className="w-6 h-6 text-white" />
          </div>
          <div>
            <span>{T.title}</span>
            <p className="text-sm text-gray-500 font-normal mt-1">{T.subtitle}</p>
          </div>
        </h1>
      </div>

      <div className="flex gap-2 border-b border-gray-200 overflow-x-auto">
        {tabs.map(tab => {
          const Icon = tab.icon;
          return (
            <button key={tab.id} onClick={() => setActiveTab(tab.id)}
              className={`flex items-center gap-2 px-6 py-3 font-medium transition border-b-2 whitespace-nowrap ${activeTab === tab.id ? 'border-slate-900 text-slate-900' : 'border-transparent text-gray-500 hover:text-gray-700'}`}>
              <Icon className="w-4 h-4" />
              <span>{tab.label}</span>
            </button>
          );
        })}
      </div>

      {activeTab === 'company' && (
        <Card className="border-0 shadow-lg">
          <CardContent className="p-6">
            <h2 className="text-lg font-bold mb-4 flex items-center gap-2"><Building2 className="w-5 h-5 text-slate-600" />{T.companyInfo}</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div><Label>{T.companyName} *</Label><Input value={companyInfo.companyName} onChange={(e) => setCompanyInfo({...companyInfo, companyName: e.target.value})} className={inputClass} placeholder="Target Express" /></div>
              <div><Label>{T.companyEmail} *</Label><Input value={companyInfo.companyEmail} onChange={(e) => setCompanyInfo({...companyInfo, companyEmail: e.target.value})} className={inputClass} placeholder="info@targetexpress.com" /></div>
              <div><Label>{T.companyPhone} *</Label><Input value={companyInfo.companyPhone} onChange={(e) => setCompanyInfo({...companyInfo, companyPhone: e.target.value})} className={inputClass} placeholder="+20 100 000 0000" /></div>
              <div><Label>{T.companyWebsite}</Label><Input value={companyInfo.companyWebsite} onChange={(e) => setCompanyInfo({...companyInfo, companyWebsite: e.target.value})} className={inputClass} placeholder="https://targetexpress.com" /></div>
              <div className="md:col-span-2"><Label>{T.companyAddress}</Label><textarea value={companyInfo.companyAddress} onChange={(e) => setCompanyInfo({...companyInfo, companyAddress: e.target.value})} className={inputClass} rows={2} placeholder={"العنوان الكامل"} /></div>
              <div><Label>{T.taxNumber}</Label><Input value={companyInfo.taxNumber} onChange={(e) => setCompanyInfo({...companyInfo, taxNumber: e.target.value})} className={inputClass} placeholder="Tax ID" /></div>
              <div><Label>{T.companyLogo}</Label><Input value={companyInfo.companyLogo} onChange={(e) => setCompanyInfo({...companyInfo, companyLogo: e.target.value})} className={inputClass} placeholder="https://..." /></div>
            </div>
            <SaveButton onClick={saveCompanyInfo} />
          </CardContent>
        </Card>
      )}

      {activeTab === 'general' && (
        <Card className="border-0 shadow-lg">
          <CardContent className="p-6">
            <h2 className="text-lg font-bold mb-4 flex items-center gap-2"><Globe className="w-5 h-5 text-slate-600" />{T.generalSettings}</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div><Label>{T.currency}</Label><select value={generalSettings.currency} onChange={(e) => setGeneralSettings({...generalSettings, currency: e.target.value})} className={inputClass}><option value="EGP">{"جنيه مصري (EGP)"}</option><option value="USD">{"دولار أمريكي (USD)"}</option><option value="EUR">{"يورو (EUR)"}</option><option value="SAR">{"ريال سعودي (SAR)"}</option></select></div>
              <div><Label>{T.language}</Label><select value={generalSettings.language} onChange={(e) => setGeneralSettings({...generalSettings, language: e.target.value})} className={inputClass}><option value="ar">{"العربية"}</option><option value="en">English</option></select></div>
              <div><Label>{T.timezone}</Label><select value={generalSettings.timezone} onChange={(e) => setGeneralSettings({...generalSettings, timezone: e.target.value})} className={inputClass}><option value="Africa/Cairo">{"القاهرة (GMT+2)"}</option><option value="Asia/Riyadh">{"الرياض (GMT+3)"}</option><option value="Asia/Dubai">{"دبي (GMT+4)"}</option></select></div>
              <div><Label>{T.dateFormat}</Label><select value={generalSettings.dateFormat} onChange={(e) => setGeneralSettings({...generalSettings, dateFormat: e.target.value})} className={inputClass}><option value="DD/MM/YYYY">DD/MM/YYYY</option><option value="MM/DD/YYYY">MM/DD/YYYY</option><option value="YYYY-MM-DD">YYYY-MM-DD</option></select></div>
            </div>
            <SaveButton onClick={saveGeneralSettings} />
          </CardContent>
        </Card>
      )}

      {activeTab === 'notifications' && (
        <Card className="border-0 shadow-lg">
          <CardContent className="p-6">
            <h2 className="text-lg font-bold mb-4 flex items-center gap-2"><Bell className="w-5 h-5 text-slate-600" />{T.notificationSettings}</h2>
            <div className="space-y-4">
              {[
                { key: 'emailNotifications', label: T.emailNotifications, desc: "استقبال التحديثات عبر البريد", icon: Mail },
                { key: 'smsNotifications', label: T.smsNotifications, desc: "استقبال التحديثات عبر SMS", icon: Phone },
                { key: 'shipmentUpdates', label: T.shipmentUpdates, desc: "تلقي إشعارات تغيير حالة الشحنات", icon: Package },
                { key: 'paymentAlerts', label: T.paymentAlerts, desc: "تلقي إشعارات عن المدفوعات", icon: Receipt },
                { key: 'dailyReports', label: T.dailyReports, desc: "استقبال تقارير يومية", icon: FileText }
              ].map(item => {
                const Icon = item.icon;
                const value = notificationSettings[item.key as keyof typeof notificationSettings];
                return (
                  <div key={item.key} className="flex items-center justify-between p-4 bg-gray-50 rounded-xl">
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 rounded-lg bg-white flex items-center justify-center"><Icon className="w-5 h-5 text-slate-600" /></div>
                      <div><p className="font-semibold text-gray-900">{item.label}</p><p className="text-sm text-gray-500">{item.desc}</p></div>
                    </div>
                    <Toggle checked={value} onChange={(v) => setNotificationSettings({...notificationSettings, [item.key]: v})} />
                  </div>
                );
              })}
            </div>
            <SaveButton onClick={saveNotificationSettings} />
          </CardContent>
        </Card>
      )}

      {activeTab === 'printing' && (
        <Card className="border-0 shadow-lg">
          <CardContent className="p-6">
            <h2 className="text-lg font-bold mb-4 flex items-center gap-2"><Printer className="w-5 h-5 text-slate-600" />{T.printSettings}</h2>
            <div className="space-y-4">
              <div><Label>{T.paperSize}</Label><select value={printSettings.paperSize} onChange={(e) => setPrintSettings({...printSettings, paperSize: e.target.value})} className={inputClass}><option value="A4">A4</option><option value="A5">A5</option><option value="Letter">Letter</option></select></div>
              {[
                { key: 'includeLogo', label: T.includeLogo },
                { key: 'includeFooter', label: T.includeFooter },
                { key: 'showBarcode', label: T.showBarcode },
                { key: 'showQRCode', label: T.showQRCode }
              ].map(item => {
                const value = printSettings[item.key as keyof typeof printSettings];
                return (
                  <div key={item.key} className="flex items-center justify-between p-4 bg-gray-50 rounded-xl">
                    <p className="font-semibold text-gray-900">{item.label}</p>
                    <Toggle checked={value as boolean} onChange={(v) => setPrintSettings({...printSettings, [item.key]: v})} />
                  </div>
                );
              })}
            </div>
            <SaveButton onClick={savePrintSettings} />
          </CardContent>
        </Card>
      )}

      {activeTab === 'security' && (
        <Card className="border-0 shadow-lg">
          <CardContent className="p-6">
            <h2 className="text-lg font-bold mb-4 flex items-center gap-2"><Shield className="w-5 h-5 text-slate-600" />{T.securitySettings}</h2>
            <div className="space-y-4">
              <div className="p-4 bg-amber-50 border border-amber-200 rounded-xl">
                <p className="font-semibold text-amber-900">{T.passwordPolicy}</p>
                <p className="text-sm text-amber-700 mt-1">{"على الأقل 8 أحرف مع حروف كبيرة وصغيرة وأرقام ورموز خاصة"}</p>
                <select value={securitySettings.passwordPolicy} onChange={(e) => setSecuritySettings({...securitySettings, passwordPolicy: e.target.value})} className={inputClass + ' mt-2'}>
                  <option value="basic">{"أساسي (4 أحرف)"}</option>
                  <option value="medium">{"متوسط (6 أحرف)"}</option>
                  <option value="strong">{"قوي (8 أحرف + رموز)"}</option>
                </select>
              </div>
              <div className="p-4 bg-blue-50 border border-blue-200 rounded-xl">
                <div className="flex items-center justify-between">
                  <div><p className="font-semibold text-blue-900">{T.twoFactor}</p><p className="text-sm text-blue-700 mt-1">{"إضافة طبقة أمان إضافية لحسابك"}</p></div>
                  <Toggle checked={securitySettings.enable2FA} onChange={(v) => setSecuritySettings({...securitySettings, enable2FA: v})} />
                </div>
              </div>
              <div className="p-4 bg-emerald-50 border border-emerald-200 rounded-xl">
                <p className="font-semibold text-emerald-900">{T.sessionTimeout}</p>
                <p className="text-sm text-emerald-700 mt-1">{"تسجيل الخروج تلقائياً بعد عدم النشاط"}</p>
                <select value={securitySettings.sessionTimeout} onChange={(e) => setSecuritySettings({...securitySettings, sessionTimeout: e.target.value})} className={inputClass + ' mt-2'}>
                  <option value="15">{"15 دقيقة"}</option>
                  <option value="30">{"30 دقيقة"}</option>
                  <option value="60">{"ساعة"}</option>
                  <option value="120">{"ساعتان"}</option>
                </select>
              </div>
            </div>
            <SaveButton onClick={saveSecuritySettings} />
          </CardContent>
        </Card>
      )}
    </div>
  );
}
